import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { AiReceptionistOrg } from '@wetags/ai-receptionist';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

const ENABLED_CF_STATES = [State.ACTIVE, State.PROMOTE];

const useLocationCustomizationFlags = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlag } = useAppFlagStore();
  const { isFlagStoreLoaded } = useScopedAppFlagStore();

  return useMemo(() => {
    const hasEnabledFlag = (featureEnum: Feature, locationId: string) => {
      const [flag] = getCustomizationFlag({ locationIds: [locationId], featureEnum });
      return !!flag?.state && ENABLED_CF_STATES.includes(flag.state);
    };

    const airCfEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      [Feature.TRUELARK_AI_MESSAGING, Feature.TRUELARK_AI_VOICE].some((featureEnum) =>
        hasEnabledFlag(featureEnum, locationId)
      )
    );

    const weaveAirCfEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      [Feature.WEAVE_MESSAGING_AIR, Feature.WEAVE_VOICE_AIR].some((featureEnum) =>
        hasEnabledFlag(featureEnum, locationId)
      )
    );

    const onlineSchedulingEnabledLocationIds = selectedLocationIds.filter((locationId) =>
      hasEnabledFlag(Feature.ONLINE_SCHEDULING, locationId)
    );

    return {
      airCfEnabledLocationIds,
      weaveAirCfEnabledLocationIds,
      onlineSchedulingEnabledLocationIds,
      isLoading: !isFlagStoreLoaded,
    };
  }, [getCustomizationFlag, isFlagStoreLoaded, selectedLocationIds]);
};

const usePreferenceRequestAirAccess = () => {
  const [isAirSettingsWetagEnabled, isLoadingisAirSettingsWetag] = AiReceptionistOrg.useWetag('air-settings');
  const { airCfEnabledLocationIds, isLoading: isLoadingAirCfEnabledLocationIds } = useLocationCustomizationFlags();

  const airEnabledLocationIds = isAirSettingsWetagEnabled ? airCfEnabledLocationIds : [];
  const isLoadingAirEnabledLocationIds = isLoadingAirCfEnabledLocationIds || isLoadingisAirSettingsWetag;

  return {
    airEnabledLocationIds,
    isLoadingAirEnabledLocationIds,
  };
};

// custom hook to check access for schedule request features
export const useScheduleRequestFeaturesAccess = () => {
  const { airEnabledLocationIds, isLoadingAirEnabledLocationIds } = usePreferenceRequestAirAccess();
  const {
    weaveAirCfEnabledLocationIds,
    onlineSchedulingEnabledLocationIds,
    isLoading: isLoadingCustomizationFlags,
  } = useLocationCustomizationFlags();

  const enabledLocationIds = Array.from(
    new Set([...onlineSchedulingEnabledLocationIds, ...airEnabledLocationIds, ...weaveAirCfEnabledLocationIds])
  );

  const hasAccess = enabledLocationIds.length > 0;
  const isLoading = isLoadingAirEnabledLocationIds || isLoadingCustomizationFlags;

  return {
    isLoading,
    hasScheduleRequestFeaturesAccess: !isLoading && hasAccess,
    scheduleRequestFeatureEnabledLocationIds: enabledLocationIds,
  };
};
